import {User, LogOut, Save, Mail} from "lucide-react";
import {useMemo, useState, useEffect} from "react";

const ThreeDots = ({className, color}) => (
  <div className={`${className}`}>
    <svg
      width="32"
      height="8"
      viewBox="0 0 120 30"
      xmlns="http://www.w3.org/2000/svg"
      fill={color || "#ffffff"}
    >
      <circle cx="15" cy="15" r="15">
        <animate
          attributeName="r"
          from="15"
          to="15"
          begin="0s"
          dur="0.8s"
          values="15;9;15"
          calcMode="linear"
          repeatCount="indefinite"
        />
        <animate
          attributeName="fill-opacity"
          from="1"
          to="1"
          begin="0s"
          dur="0.8s"
          values="1;.5;1"
          calcMode="linear"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="60" cy="15" r="9" fillOpacity="0.3">
        <animate
          attributeName="r"
          from="9"
          to="9"
          begin="0s"
          dur="0.8s"
          values="9;15;9"
          calcMode="linear"
          repeatCount="indefinite"
        />
        <animate
          attributeName="fill-opacity"
          from="0.5"
          to="0.5"
          begin="0s"
          dur="0.8s"
          values=".5;1;.5"
          calcMode="linear"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="105" cy="15" r="15">
        <animate
          attributeName="r"
          from="15"
          to="15"
          begin="0s"
          dur="0.8s"
          values="15;9;15"
          calcMode="linear"
          repeatCount="indefinite"
        />
        <animate
          attributeName="fill-opacity"
          from="1"
          to="1"
          begin="0s"
          dur="0.8s"
          values="1;.5;1"
          calcMode="linear"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  </div>
);

const Settings = ({
  formData,
  COUNTRIES,
  onSignOut,
  onUpdateProfile,
  isActionLoading,
}) => {
  const [name, setName] = useState({
    firstname: formData.firstname,
    lastname: formData.lastname,
  });

  useEffect(() => {
    setName({firstname: formData.firstname, lastname: formData.lastname});
  }, [formData.firstname, formData.lastname]);

  const currentCountry = useMemo(
    () =>
      COUNTRIES.find((c) => c.code === formData.country_code) || COUNTRIES[0],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData.country_code],
  );

  // No min-h-screen: this view flows inside the app's scrolling <main>,
  // and 100vh overshoots the visible mobile viewport (shell is 100dvh).
  return (
    <div className="flex-1 bg-white relative">
      <main className="max-w-4xl mx-auto py-2 md:py-12 px-0 md:px-12 space-y-6 md:space-y-20">
        {/* Row 1: Personal Details */}
        <div className="flex flex-col lg:grid lg:grid-cols-12 gap-4 lg:gap-16">
          <div className="lg:col-span-4">
            <h2 className="text-2xl font-bold text-slate-900 mb-1 lg:mb-2">
              Personal Details
            </h2>
            <p className="text-slate-500 text-sm leading-relaxed lg:mb-8">
              Manage your personal information and account settings
            </p>

            {/* Desktop Save Button */}
            <button
              onClick={() => onUpdateProfile(name)}
              disabled={isActionLoading}
              className="hidden lg:flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-2xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isActionLoading ? (
                <ThreeDots className="flex px-10 py-1" color="#ffffff" />
              ) : (
                <>
                  <Save size={18} /> Save Changes
                </>
              )}
            </button>
          </div>

          <div className="lg:col-span-8 space-y-4 lg:space-y-10">
            {/* Form Inputs */}
            <div className="space-y-3 lg:space-y-6">
              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-slate-800 ml-1">
                  First name
                </label>
                <div className="relative">
                  <User
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    size={18}
                  />
                  <input
                    type="text"
                    value={name.firstname || ""}
                    onChange={(e) =>
                      setName((p) => ({...p, firstname: e.target.value}))
                    }
                    className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-3xl outline-none text-slate-900"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-slate-800 ml-1">
                  Last name
                </label>
                <div className="relative">
                  <User
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    size={18}
                  />
                  <input
                    type="text"
                    value={name.lastname || ""}
                    onChange={(e) =>
                      setName((p) => ({...p, lastname: e.target.value}))
                    }
                    className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-3xl outline-none text-slate-900"
                  />
                </div>
              </div>

              {/* The row shows the sign-in identity (read-only): phone
                  accounts get the phone row, email accounts the email row. */}
              {formData.phone ? (
                <div className="space-y-1.5">
                  <label className="block text-sm font-semibold text-slate-800 ml-1">
                    Phone number
                  </label>
                  <div className="flex items-center border border-slate-200 rounded-3xl px-4 py-3 bg-white">
                    <div className="flex items-center gap-2 pr-3 mr-3 border-r border-slate-100">
                      <span className="text-xl inline-block w-6 text-center">
                        <img
                          src={currentCountry.flag}
                          alt={`${currentCountry.countryName}`}
                          className="object-cover w-full h-full"
                        />
                      </span>
                      <span className="font-bold text-slate-900">
                        {currentCountry.dialCode}
                      </span>
                    </div>
                    <span className="flex-1 text-slate-500 font-medium">
                      {formData.phone}
                    </span>
                  </div>
                </div>
              ) : formData.email ? (
                <div className="space-y-1.5">
                  <label className="block text-sm font-semibold text-slate-800 ml-1">
                    Email
                  </label>
                  <div className="flex items-center border border-slate-200 rounded-3xl px-4 py-3 bg-white">
                    <Mail size={18} className="text-slate-400 mr-3" />
                    <span className="flex-1 text-slate-500 font-medium">
                      {formData.email}
                    </span>
                  </div>
                </div>
              ) : null}

              {/* Mobile Save Button */}
              <button
                onClick={() => onUpdateProfile(name)}
                disabled={isActionLoading}
                className="lg:hidden w-full h-14 flex items-center justify-center gap-2 px-6 bg-indigo-600 text-white rounded-3xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isActionLoading ? (
                  <ThreeDots />
                ) : (
                  <>
                    <Save size={18} /> Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Row 2: Log out */}
        <div className="pt-4 md:pt-12 border-t border-slate-50">
          <button
            onClick={onSignOut}
            className="w-full lg:w-auto py-3 px-8 bg-[#f4f7ff] text-slate-900 rounded-full font-bold flex items-center justify-center gap-2 shadow-sm"
          >
            <LogOut size={20} /> Log out
          </button>
        </div>
      </main>
    </div>
  );
};

export default Settings;
